import React from 'react';
import moment from 'moment';
import NumberFormat from 'react-number-format';

import { CURRENCY } from '../../../../../shared/enums/currency.enum';
import { PaymentGateway } from '../../../../../shared/enums/payment-gateway.enum';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import { PaymentModel } from '../../../../../shared/models/payment.model';
import { paymentTypeOptions } from '../../reservation-edit.helper';
import { PaymentActionsDropdown } from './payment-actions-dropdown.component';
import { PaymentStatusBadge } from './payment-status-badge.component';

interface PaymentsTableProps {
  payments: PaymentModel[];
  reservationSource?: string;
  isPermited: (roles: UserRole[]) => boolean;
  checkingPaymobPaymentId: string;
  switchingGatewayPaymentId: string;
  onViewDetails: (payment: PaymentModel) => void;
  onSend: (payment: PaymentModel) => void;
  onPartial: (payment: PaymentModel) => void;
  onSplitPayment: (payment: PaymentModel) => void;
  onGenerateLink: (payment: PaymentModel) => void;
  onAddReceipt: (payment: PaymentModel) => void;
  onViewHistory: (payment: PaymentModel) => void;
  onViewVatBreakdown: (payment: PaymentModel) => void;
  onResendConfirmation: (payment: PaymentModel) => void;
  onCheckPaymobStatus: (payment: PaymentModel) => void;
  onSwitchGateway: (payment: PaymentModel, targetGateway: PaymentGateway) => void;
  onMoveToReservation: (payment: PaymentModel) => void;
}

function getPaymentMethod(payment: PaymentModel): string {
  if (payment.attempts && payment.attempts.length > 0) {
    for (let index = payment.attempts.length - 1; index >= 0; index--) {
      const attempt = payment.attempts[index];
      if (attempt.cardNumber) {
        return attempt.cardNumber;
      }
    }
  }

  const typeOption = paymentTypeOptions.find((option) => option.value === payment.paymentType);
  return typeOption ? typeOption.label : payment.paymentType || 'N/A';
}

function getPaymentGateway(payment: PaymentModel): string {
  if (payment.paymentGateway === 'paymob') {
    return 'Paymob';
  }
  if (payment.paymentGateway === 'geidea') {
    return 'Geidea';
  }
  return 'N/A';
}

function getDescription(payment: PaymentModel): string {
  if (payment.paymentItems && payment.paymentItems.length > 0) {
    const names = payment.paymentItems.filter((item) => item.feeName).map((item) => item.feeName);
    if (names.length > 0) {
      return names.length > 2 ? `${names[0]}, ${names[1]}...` : names.join(', ');
    }
  }
  return payment.direction === 'out' ? 'Refund' : 'Payment';
}

function getDueDate(payment: PaymentModel): string {
  if (payment.dueDate) {
    return moment(payment.dueDate).format('D MMM, YYYY');
  }
  return 'N/A';
}

function getCurrencyPrefix(currency?: string): string {
  return currency ? `${CURRENCY[currency as keyof typeof CURRENCY]} ` : 'EGP ';
}

const headerClassName =
  'px-[16px] py-[12px] text-left text-[12px] font-gilroy-medium text-neutral-300 whitespace-nowrap';

const cellClassName =
  'px-[16px] py-[14px] text-[14px] text-neutral-500 whitespace-nowrap align-middle';

export function PaymentsTable({
  payments,
  reservationSource,
  isPermited,
  checkingPaymobPaymentId,
  switchingGatewayPaymentId,
  onViewDetails,
  onSend,
  onPartial,
  onSplitPayment,
  onGenerateLink,
  onAddReceipt,
  onViewHistory,
  onViewVatBreakdown,
  onResendConfirmation,
  onCheckPaymobStatus,
  onSwitchGateway,
  onMoveToReservation
}: PaymentsTableProps): JSX.Element {
  return payments.length > 0 ? (
    <div className="overflow-x-auto rounded-[12px] border border-gray-200">
      <table className="w-full min-w-[860px] border-collapse">
        <thead className="bg-creams-100">
          <tr>
            <th className={headerClassName}>ID</th>
            <th className={headerClassName}>Method</th>
            <th className={headerClassName}>Gateway</th>
            <th className={headerClassName}>Amount</th>
            <th className={headerClassName}>Direction</th>
            <th className={headerClassName}>Status</th>
            <th className={headerClassName}>Description</th>
            <th className={headerClassName}>Due Date</th>
            <th className={`${headerClassName} text-center`}>Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {payments.map((payment, index) => (
            <tr
              key={payment.id || index}
              onClick={() => onViewDetails(payment)}
              className={`cursor-pointer transition-colors hover:bg-creams-100 ${
                payment.direction === 'out' ? 'bg-secondary-50/40' : 'bg-neutral-0'
              }`}
            >
              <td className={`${cellClassName} font-gilroy-medium max-w-[120px] truncate`}>
                {payment.id?.length > 10 ? payment.id.substring(0, 10) + '...' : payment.id}
              </td>
              <td className={`${cellClassName} font-gilroy-medium`}>{getPaymentMethod(payment)}</td>
              <td className={cellClassName}>{getPaymentGateway(payment)}</td>
              <td
                className={`${cellClassName} font-gilroy-medium ${
                  payment.paymentAmount <= 0 ? 'text-scarlet' : ''
                }`}
              >
                <NumberFormat
                  value={payment.paymentAmount || 0}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={getCurrencyPrefix(payment.currency)}
                />
              </td>
              <td className={cellClassName}>
                <span
                  className={`inline-flex items-center px-[10px] py-[3px] rounded-full text-[12px] font-gilroy-medium ${
                    payment.direction === 'out'
                      ? 'bg-secondary-50 text-secondary-700'
                      : 'bg-primary-50 text-primary-700'
                  }`}
                >
                  {payment.direction === 'out' ? 'PAY-OUT' : 'PAY-IN'}
                </span>
              </td>
              <td className={cellClassName}>
                <PaymentStatusBadge status={payment.status} />
              </td>
              <td className={`${cellClassName} max-w-[180px] truncate text-neutral-300`}>
                {getDescription(payment)}
              </td>
              <td className={`${cellClassName} text-neutral-300`}>{getDueDate(payment)}</td>
              <td className={`${cellClassName} text-center`} onClick={(e) => e.stopPropagation()}>
                <PaymentActionsDropdown
                  payment={payment}
                  isPermited={isPermited}
                  reservationSource={reservationSource}
                  isCheckingPaymobStatus={checkingPaymobPaymentId === payment.id}
                  isSwitchingGateway={switchingGatewayPaymentId === payment.id}
                  onSend={() => onSend(payment)}
                  onPartial={() => onPartial(payment)}
                  onSplitPayment={() => onSplitPayment(payment)}
                  onGenerateLink={() => onGenerateLink(payment)}
                  onAddReceipt={() => onAddReceipt(payment)}
                  onViewHistory={() => onViewHistory(payment)}
                  onViewVatBreakdown={() => onViewVatBreakdown(payment)}
                  onResendConfirmation={() => onResendConfirmation(payment)}
                  onCheckPaymobStatus={() => onCheckPaymobStatus(payment)}
                  onSwitchGateway={(targetGateway) => onSwitchGateway(payment, targetGateway)}
                  onMoveToReservation={() => onMoveToReservation(payment)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <div className="rounded-[16px] bg-creams-100 px-[24px] py-[32px] text-center text-[15px] text-neutral-300">
      No payments found
    </div>
  );
}
